import { Component } from '@theme/component';

/**
 * <newsletter-popup>
 *
 * Auto-opens a promotional newsletter dialog once per session after a
 * configurable delay. Collects email + phone via Shopify's customer form.
 * Phone number is formatted to E.164 via intl-tel-input (loaded globally
 * by popup-newsletter section) and mirrored into contact[note] as a
 * backup so the value persists on the customer record.
 *
 * Session state is tracked in sessionStorage under two keys:
 *   - newsletterPopupDismissed : set on close or on submit. Blocks reopen.
 *   - newsletterPopupSubmitted : set right before form POST. Read back after
 *                                the reload to force-open the popup in its
 *                                success state, then cleared.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLButtonElement} closeButton
 * @extends {Component<Refs>}
 */
class NewsletterPopup extends Component {
  requiredRefs = ['dialog', 'closeButton'];

  static DISMISSED_KEY = 'newsletterPopupDismissed';
  static SUBMITTED_KEY = 'newsletterPopupSubmitted';

  /** @type {any} */
  #iti = null;

  /** @type {string} */
  #countryCode = 'IN';

  /** @type {ReturnType<typeof setTimeout> | null} */
  #openTimer = null;

  connectedCallback() {
    super.connectedCallback();

    this.refs.closeButton.addEventListener('click', this.#onClose);
    this.refs.dialog.addEventListener('close', this.#onDialogClose);

    const form = this.querySelector('.newsletter-popup__form');
    if (form instanceof HTMLFormElement) {
      form.addEventListener('submit', this.#onSubmit);
    }

    if (this.dataset.formSuccess === 'true') {
      this.#clearSubmittedFlag();
      this.#open();
      return;
    }

    if (this.#isDismissed()) return;
    if (sessionStorage.getItem(NewsletterPopup.SUBMITTED_KEY) === 'true') {
      this.#clearSubmittedFlag();
      this.#markDismissed();
      return;
    }

    const delayMs = (Number(this.dataset.delaySeconds) || 3) * 1000;
    this.#openTimer = setTimeout(() => {
      if (this.#isDismissed()) return;
      this.#open();
    }, delayMs);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#openTimer) clearTimeout(this.#openTimer);
  }

  #open() {
    const dialog = this.refs.dialog;
    if (!dialog || dialog.open) return;

    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }

    this.#initPhoneInput();
  }

  #onClose = () => {
    this.#markDismissed();
    const dialog = this.refs.dialog;
    if (dialog?.open) dialog.close();
  };

  #onDialogClose = () => {
    this.#markDismissed();
  };

  async #initPhoneInput() {
    const input = this.querySelector('#customer-phone');
    if (!(input instanceof HTMLInputElement)) return;
    if (this.#iti) return;

    const itiFactory = /** @type {any} */ (window).intlTelInput;
    if (typeof itiFactory !== 'function') return;

    try {
      const res = await fetch('https://api.ipinfo.io/lite/me?token=' + encodeURIComponent(
        /** @type {any} */ (window).__NEWSLETTER_POPUP_IPINFO_TOKEN__ || ''
      ));
      if (res.ok) {
        const data = await res.json();
        if (data && data.country_code) this.#countryCode = data.country_code;
      }
    } catch (err) {
      console.warn('[newsletter-popup] geo lookup failed, defaulting to', this.#countryCode, err);
    }

    const utilsScript =
      /** @type {any} */ (window).__NEWSLETTER_POPUP_UTILS_URL__ || '';

    this.#iti = itiFactory(input, {
      initialCountry: this.#countryCode,
      separateDialCode: true,
      nationalMode: false,
      autoHideDialCode: false,
      utilsScript,
    });
  }

  /** @param {SubmitEvent} event */
  #onSubmit = (event) => {
    const form = event.currentTarget;
    if (!(form instanceof HTMLFormElement)) return;

    const phoneInput = form.querySelector('#customer-phone');
    const raw = phoneInput instanceof HTMLInputElement ? phoneInput.value.trim() : '';

    let formattedPhone = '';
    if (raw) {
      formattedPhone = this.#formatPhone(raw);
    }

    if (formattedPhone) {
      this.#upsertHidden(form, 'contact[phone]', formattedPhone);
      this.#upsertHidden(form, 'contact[note]', formattedPhone);
    }

    if (phoneInput instanceof HTMLInputElement) {
      phoneInput.removeAttribute('name');
    }

    sessionStorage.setItem(NewsletterPopup.SUBMITTED_KEY, 'true');
    this.#markDismissed();
  };

  /**
   * Format phone number to E.164 using intl-tel-input if available, else
   * a best-effort fallback based on the detected country dial code.
   * @param {string} raw
   * @returns {string}
   */
  #formatPhone(raw) {
    if (this.#iti) {
      try {
        if (typeof this.#iti.getNumber === 'function') {
          const n = this.#iti.getNumber();
          if (typeof n === 'string' && n.startsWith('+')) return n;
        }
        const country = this.#iti.getSelectedCountryData?.();
        if (country && country.dialCode) {
          const digits = raw.replace(/\D/g, '');
          if (!digits) return '';
          return digits.startsWith(country.dialCode) ? `+${digits}` : `+${country.dialCode}${digits}`;
        }
      } catch (err) {
        console.warn('[newsletter-popup] phone formatting failed, falling back', err);
      }
    }
    if (raw.startsWith('+')) return raw;
    const digits = raw.replace(/\D/g, '');
    if (!digits) return '';
    return '+' + digits;
  }

  /**
   * @param {HTMLFormElement} form
   * @param {string} name
   * @param {string} value
   */
  #upsertHidden(form, name, value) {
    let input = form.querySelector(`input[name="${CSS.escape(name)}"]`);
    if (!(input instanceof HTMLInputElement)) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }
    input.value = value;
  }

  #isDismissed() {
    return sessionStorage.getItem(NewsletterPopup.DISMISSED_KEY) === 'true';
  }

  #markDismissed() {
    sessionStorage.setItem(NewsletterPopup.DISMISSED_KEY, 'true');
  }

  #clearSubmittedFlag() {
    sessionStorage.removeItem(NewsletterPopup.SUBMITTED_KEY);
  }
}

if (!customElements.get('newsletter-popup')) {
  customElements.define('newsletter-popup', NewsletterPopup);
}
